// @ts-check
// Campos oferecidos no editor E/OU (contrato 09 de público, seção 9): nas views, os filtráveis do objeto; no público,
// os do card que o papel lê mais as relações do catálogo, com os campos do destino (um salto) e os sub-campos.
import { isAudienceRelationField, isAudienceTargetField, audienceTargetSubFields } from '../../../shared/audiences/audience-relations.js';
import { getFilterSubFields } from '../../../shared/filter/view-filter-tree.js';
import { FIELD_TYPE, isFieldDisplayable, isFieldFilterable } from './field-metadata.js';
import { canReadObjectRecords } from './permissions.js';

/**
 * @typedef {import('./field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('./field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {{ fields: FieldMetadata[], getSubFields: (field: FieldMetadata) => readonly string[],
 *   getRelationTargets?: (field: FieldMetadata) => FieldMetadata[] | null,
 *   getTargetSubFields?: (relationField: FieldMetadata, targetField: FieldMetadata) => readonly string[],
 *   getField: (fieldMetadataId: string) => FieldMetadata | undefined }} FieldCatalog
 */

/**
 * O comportamento das listas: campos filtráveis, sub-campos dos compostos, sem relações com destino.
 * @param {ObjectMetadata} objectMetadata @param {(fieldMetadataId: string) => FieldMetadata | undefined} getField
 * @returns {FieldCatalog}
 */
export function fieldCatalogForView(objectMetadata, getField) {
  return {
    fields: objectMetadata.fields.filter((field) => isFieldFilterable(field)),
    getSubFields: getFilterSubFields,
    getField,
  };
}

/** Relação um para muitos ou muitos para um, pelo formato da SPA. @param {FieldMetadata} field */
const relationTypeOf = (field) => /** @type {any} */ (field).relation?.type ?? /** @type {any} */ (field).relationType ?? null;

/**
 * O público: os campos do card que o papel lê (a SPA já recebe os metadados sem os ilegíveis) e as relações do
 * catálogo; cada relação abre os campos do destino, só de objetos que o papel lê.
 * @param {ObjectMetadata} objectMetadata
 * @param {{ objectsByNameSingular: Map<string, ObjectMetadata>, getField: (fieldMetadataId: string) => FieldMetadata | undefined }} lookups
 * @returns {FieldCatalog}
 */
export function fieldCatalogForAudience(objectMetadata, lookups) {
  /** @param {FieldMetadata} relationField */
  const targetObject = (relationField) => {
    const name = /** @type {any} */ (relationField).relation?.targetObjectNameSingular;
    const target = name ? lookups.objectsByNameSingular.get(name) : undefined;
    return target && canReadObjectRecords(target) ? target : undefined;
  };
  const fields = objectMetadata.fields.filter((field) => {
    if (field.type === FIELD_TYPE.RELATION && isAudienceRelationField(/** @type {any} */ (field))) return Boolean(targetObject(field)) || relationTypeOf(field) === 'ONE_TO_MANY';
    return isFieldFilterable(field);
  });
  return {
    fields,
    getSubFields: getFilterSubFields,
    getField: lookups.getField,
    getRelationTargets: (field) => {
      if (field.type !== FIELD_TYPE.RELATION || !isAudienceRelationField(/** @type {any} */ (field))) return null;
      const target = targetObject(field);
      if (!target) return [];
      return target.fields.filter((targetField) => isAudienceTargetField(/** @type {any} */ (field), /** @type {any} */ (targetField))
        && isFieldDisplayable(targetField)
        && (targetField.type !== FIELD_TYPE.RELATION || relationTypeOf(targetField) === 'MANY_TO_ONE'));
    },
    getTargetSubFields: (_relationField, targetField) => audienceTargetSubFields(/** @type {any} */ (targetField), getFilterSubFields(targetField)),
  };
}

/**
 * A relação é um para muitos (operandos "algum é", "nenhum é", "tem algum", "não tem nenhum")?
 * @param {FieldMetadata | undefined} field
 */
export const isOneToManyAudienceRelation = (field) => Boolean(field) && field?.type === FIELD_TYPE.RELATION && relationTypeOf(/** @type {FieldMetadata} */ (field)) === 'ONE_TO_MANY';
